import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, FileText, ImageIcon, Paperclip, X } from "lucide-react";

/**
 * Proof viewing on Screen 6.
 *
 * Three rules from the PRD drive this component:
 *
 *   - Inline, not a redirect. An image opens in a preview panel and a PDF in
 *     an inline viewer, both on this screen; neither hands the approver off to
 *     a document-management screen.
 *   - Stay on screen. The panel is a sibling of the claim body, so the claim
 *     stays behind it and the approver never loses their place.
 *   - Failure is visible. A proof that will not render shows a stated
 *     "unavailable" cell, never a silent blank — that is itself a signal the
 *     approver may want to Send Back.
 *
 * Whether a proof exists at all is the data's business: with no proof rows the
 * cell states that none is attached, and says whether the category required
 * one, rather than implying anything about the claim.
 */

const isImage = (proof) => String(proof?.mimeType || "").startsWith("image/");
const isPdf = (proof) => String(proof?.mimeType || "") === "application/pdf";

/** One proof, rendered inline. Reports upward when it cannot be displayed. */
function ProofSurface({ proof }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);

  if (!proof?.url || failed) {
    return (
      <div className="grid h-[320px] place-items-center rounded-xl border border-dashed border-rose-200 bg-rose-50/60 px-6 text-center">
        <div>
          <p className="text-sm font-semibold text-rose-700">
            {t("conveyanceApproval.proofUnavailable")}
          </p>
          <p className="mt-1 text-[12px] text-rose-600/90">
            {t("conveyanceApproval.proofUnavailableHint")}
          </p>
        </div>
      </div>
    );
  }

  if (isImage(proof)) {
    return (
      <img
        src={proof.url}
        alt={proof.fileName || t("conveyanceApproval.proof")}
        onError={() => setFailed(true)}
        className="max-h-[60vh] w-full rounded-xl border border-[var(--color-text-border)]/60 object-contain bg-[var(--color-workspace)]/30"
      />
    );
  }

  if (isPdf(proof)) {
    return (
      <object
        data={proof.url}
        type="application/pdf"
        aria-label={proof.fileName || t("conveyanceApproval.proof")}
        className="h-[60vh] w-full rounded-xl border border-[var(--color-text-border)]/60"
      >
        {/* Rendered by browsers that will not embed a PDF — still no redirect,
            just an explicit statement plus the download the panel offers. */}
        <div className="grid h-full place-items-center px-6 text-center">
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t("conveyanceApproval.proofInlineUnsupported")}
          </p>
        </div>
      </object>
    );
  }

  return (
    <div className="grid h-[320px] place-items-center rounded-xl border border-[var(--color-text-border)]/60 bg-[var(--color-workspace)]/30 px-6 text-center">
      <p className="text-sm text-[var(--color-text-secondary)]">
        {t("conveyanceApproval.proofInlineUnsupported")}
      </p>
    </div>
  );
}

/** The slide-over that keeps the approver on Screen 6 while they read a proof. */
export function ProofPreviewPanel({ proof, onClose }) {
  const { t } = useTranslation();
  if (!proof) return null;

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label={t("formActions.close")}
        onClick={onClose}
        className="absolute inset-0 bg-[#0a1230]/50 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-[860px] max-h-[90vh] overflow-hidden flex flex-col rounded-2xl border border-white/60 bg-white shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)]">
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-[var(--color-text-border)]/70 shrink-0">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">
              {proof.fileName || t("conveyanceApproval.proof")}
            </p>
            {proof.fileSize ? (
              <p className="text-[11px] text-[var(--color-text-secondary)]">
                {Math.max(1, Math.round(Number(proof.fileSize) / 1024))} KB
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {proof.url ? (
              <a
                href={proof.url}
                download={proof.fileName || true}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[var(--color-text-border)] text-[12px] font-bold text-foreground hover:border-[var(--color-primary)]/40 hover:text-[var(--color-primary)] transition"
              >
                <Download className="h-3.5 w-3.5" />
                {t("conveyanceApproval.download")}
              </a>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="h-9 w-9 grid place-items-center rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/60 hover:text-[var(--color-primary)] transition cursor-pointer"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">{t("formActions.close")}</span>
            </button>
          </div>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <ProofSurface proof={proof} />
        </div>
      </div>
    </div>
  );
}

/**
 * The Proof cell inside a claim body row. `required` only changes what the
 * empty state says — a missing mandatory proof reads differently from an
 * optional one that was simply not attached.
 */
export function ProofCell({ proofs = [], required = false, onOpen }) {
  const { t } = useTranslation();

  if (proofs.length === 0) {
    return (
      <span
        className={`text-[12px] ${
          required ? "font-semibold text-rose-600" : "text-[var(--color-text-secondary)]"
        }`}
      >
        {required
          ? t("conveyanceApproval.proofMissing")
          : t("conveyanceApproval.proofNotAttached")}
      </span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {proofs.map((proof, index) => {
        const Icon = isImage(proof) ? ImageIcon : isPdf(proof) ? FileText : Paperclip;
        return (
          <button
            key={proof.proofId ?? `${proof.fileName}-${index}`}
            type="button"
            onClick={() => onOpen(proof)}
            title={proof.fileName || t("conveyanceApproval.proof")}
            className="inline-flex items-center gap-1.5 h-7 max-w-[150px] px-2 rounded-lg border border-[var(--color-text-border)] text-[11px] font-semibold text-foreground hover:border-[var(--color-primary)]/40 hover:text-[var(--color-primary)] transition cursor-pointer"
          >
            <Icon className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              {proof.fileName || t("conveyanceApproval.viewProof")}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default ProofPreviewPanel;
